import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Checkbox } from "@/components/ui/checkbox";
import {
  ArrowLeft,
  ShieldCheck,
  Eye,
  PencilLine,
  Trash2,
  MessageSquare,
  History as HistoryIcon,
  SlidersHorizontal,
  Check,
} from "lucide-react";
import DashboardLayout from "../components/layout/DashboardLayout";
import CollaborationPanel from "../components/advisor/CollaborationPanel";
import { ADVISOR_MODULE_OPTIONS } from "../components/advisor/AdvisorConnectModal";
import { api, formatApiError, fmtINR } from "../lib/api";
import { toast } from "sonner";

// Modules whose individual records the client can selectively share.
const RECORD_CONFIG = {
  assets: {
    url: "/assets",
    key: "assets",
    label: (r) => r.name,
    sub: (r) => fmtINR(r.value),
  },
  liabilities: {
    url: "/liabilities",
    key: "liabilities",
    label: (r) => r.name,
    sub: (r) => fmtINR(r.outstanding),
  },
  insurance: {
    url: "/insurance",
    key: "policies",
    label: (r) => r.name,
    sub: (r) => r.policy_type,
  },
  goals: {
    url: "/goals",
    key: "goals",
    label: (r) => r.name,
    sub: (r) => fmtINR(r.target_amount),
  },
  documents: {
    url: "/vault/documents",
    key: "documents",
    label: (r) => r.title,
    sub: (r) => r.category || "",
  },
};

const TABS = [
  { key: "sharing", label: "Sharing & Access", icon: SlidersHorizontal },
  { key: "messages", label: "Messages & Tasks", icon: MessageSquare },
  { key: "activity", label: "Activity", icon: HistoryIcon },
];

export default function AdvisorDetail() {
  const { connId } = useParams();
  const navigate = useNavigate();

  const [conn, setConn] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("sharing");
  const [history, setHistory] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/connections");
      const found = (data.connections || []).find((c) => c.id === connId);
      setConn(found || null);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }, [connId]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (tab !== "activity" || !conn) return;
    (async () => {
      try {
        const { data } = await api.get(`/connections/${connId}/history`);
        setHistory(data.history || []);
      } catch (_) {}
    })();
  }, [tab, conn, connId]);

  const patch = async (payload) => {
    try {
      const { data } = await api.patch(`/connections/${connId}`, payload);
      setConn(data);
      window.dispatchEvent(new CustomEvent("pfa:approvals-changed"));
      return true;
    } catch (err) {
      toast.error(formatApiError(err));
      return false;
    }
  };

  const toggleModule = (key) =>
    patch({
      modules: { ...(conn.modules || {}), [key]: !conn.modules?.[key] },
    });

  const setPermission = async (level) => {
    if (await patch({ permission_level: level }))
      toast.success("Permission updated");
  };

  // ids === null  -> share all of that module
  // ids === [..]  -> share only those records
  const setScope = (key, ids) => patch({ shared_ids: { [key]: ids } });

  const revoke = async () => {
    if (
      !window.confirm(
        "Revoke all access for this advisor? You can reconnect later.",
      )
    )
      return;
    try {
      await api.delete(`/connections/${connId}`);
      toast.success("Access revoked");
      navigate("/dashboard/access");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="py-16 text-center text-slate-400">Loading…</div>
      </DashboardLayout>
    );
  }

  if (!conn || conn.status !== "active") {
    return (
      <DashboardLayout>
        <div className="max-w-3xl mx-auto py-16 text-center">
          <p className="text-slate-500">
            This advisor connection is no longer active.
          </p>
          <button
            onClick={() => navigate("/dashboard/access")}
            className="btn-primary mt-4"
          >
            Back to Access Management
          </button>
        </div>
      </DashboardLayout>
    );
  }

  const canSuggest = conn.permission_level === "suggest";
  const name = conn.advisor_display_name || conn.advisor_name || "Advisor";

  return (
    <DashboardLayout>
      <div className="max-w-3xl mx-auto">
        {/* BACK */}
        <button
          onClick={() => navigate("/dashboard/access")}
          className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-emerald-600 mb-4"
        >
          <ArrowLeft className="h-4 w-4" /> All advisors
        </button>

        {/* HEADER CARD */}
        <div className="rounded-3xl bg-gradient-to-br from-emerald-600 to-blue-600 text-white p-6 mb-5">
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-center gap-4 min-w-0">
              <span className="grid h-14 w-14 shrink-0 place-items-center rounded-2xl bg-white/15 text-2xl font-bold">
                {name[0]?.toUpperCase()}
              </span>
              <div className="min-w-0">
                <h1 className="font-display text-2xl font-bold truncate">
                  {name}
                </h1>
                {conn.advisor_firm && (
                  <div className="text-white/85 text-sm">
                    {conn.advisor_firm}
                  </div>
                )}
                <div className="mt-1 inline-flex items-center gap-1.5 rounded-full bg-white/15 px-2.5 py-0.5 text-xs font-medium">
                  {canSuggest ? (
                    <>
                      <PencilLine className="h-3 w-3" /> View + Edit
                    </>
                  ) : (
                    <>
                      <Eye className="h-3 w-3" /> View only
                    </>
                  )}
                </div>
              </div>
            </div>
            <div className="text-right shrink-0">
              <div className="text-[10px] uppercase tracking-wider text-white/70">
                Advisor code
              </div>
              <div className="font-mono font-bold">{conn.advisor_code}</div>
            </div>
          </div>
        </div>

        {/* TABS */}
        <div className="flex gap-1 border-b border-slate-200 dark:border-slate-800 mb-5">
          {TABS.map((t) => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition ${
                tab === t.key
                  ? "border-emerald-500 text-emerald-700 dark:text-emerald-300"
                  : "border-transparent text-slate-500 hover:text-slate-700 dark:hover:text-slate-300"
              }`}
            >
              <t.icon className="h-4 w-4" />
              {t.label}
            </button>
          ))}
        </div>

        {tab === "sharing" && (
          <div className="space-y-5">
            {/* PERMISSION */}
            <div className="card-soft p-5">
              <div className="text-sm font-semibold text-slate-900 dark:text-white mb-1">
                Permission level
              </div>
              <p className="text-xs text-slate-500 mb-3">
                Controls whether this advisor can propose changes (you always
                approve them first).
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <PermCard
                  active={!canSuggest}
                  onClick={() => setPermission("view")}
                  icon={Eye}
                  title="View Only"
                  desc="Can see shared data, cannot propose changes."
                />
                <PermCard
                  active={canSuggest}
                  onClick={() => setPermission("suggest")}
                  icon={PencilLine}
                  title="View + Edit"
                  desc="Can propose changes for your approval."
                />
              </div>
            </div>

            {/* MODULES */}
            <div className="card-soft p-5">
              <div className="text-sm font-semibold text-slate-900 dark:text-white mb-1">
                Shared modules
              </div>
              <p className="text-xs text-slate-500 mb-3">
                Toggle what this advisor can see. For some modules you can share
                only specific records.
              </p>
              <div className="space-y-2">
                {ADVISOR_MODULE_OPTIONS.map((m) => (
                  <ModuleShareRow
                    key={m.key}
                    module={m}
                    enabled={!!conn.modules?.[m.key]}
                    selection={conn.shared_ids?.[m.key]}
                    onToggle={() => toggleModule(m.key)}
                    onSetScope={(ids) => setScope(m.key, ids)}
                  />
                ))}
              </div>
            </div>

            {/* REVOKE */}
            <div className="card-soft p-5 border border-rose-200 dark:border-rose-500/20 bg-rose-50/40 dark:bg-rose-500/5">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <div className="text-sm font-semibold text-rose-700 dark:text-rose-300">
                    Revoke access
                  </div>
                  <p className="text-xs text-slate-500">
                    Immediately stops all access and rejects pending requests.
                  </p>
                </div>
                <button
                  onClick={revoke}
                  className="flex items-center gap-1.5 rounded-xl bg-rose-500 hover:bg-rose-600 text-white text-sm font-semibold px-4 py-2.5"
                >
                  <Trash2 className="h-4 w-4" /> Revoke
                </button>
              </div>
            </div>
          </div>
        )}

        {tab === "messages" && (
          <div className="card-soft p-0 overflow-hidden">
            <CollaborationPanel
              connectionId={connId}
              role="client"
              counterpart={name}
            />
          </div>
        )}

        {tab === "activity" && (
          <div className="card-soft p-5">
            {history.length === 0 ? (
              <p className="text-sm text-slate-400 text-center py-6">
                No activity yet.
              </p>
            ) : (
              <div className="space-y-3">
                {history.map((h) => (
                  <div key={h.id} className="flex gap-3">
                    <div className="mt-1 h-2 w-2 rounded-full bg-emerald-500 shrink-0" />
                    <div>
                      <div className="text-sm text-slate-800 dark:text-slate-200">
                        {actionLabel(h.action, h.detail)}
                      </div>
                      <div className="text-[11px] text-slate-400">
                        {new Date(h.created_at).toLocaleString()} · {h.actor}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}

function ModuleShareRow({ module, enabled, selection, onToggle, onSetScope }) {
  const recordCfg = RECORD_CONFIG[module.key];
  const isSelected = Array.isArray(selection);
  return (
    <div className="rounded-xl border border-slate-200 dark:border-slate-700">
      <div className="flex items-center justify-between px-4 py-3">
        <div>
          <span className="text-sm font-medium text-slate-800 dark:text-slate-200">
            {module.label}
          </span>
          {enabled && recordCfg && (
            <div className="text-[11px] text-slate-400 mt-0.5">
              {isSelected
                ? `${selection.length} record${selection.length === 1 ? "" : "s"} shared`
                : "All records shared"}
            </div>
          )}
        </div>
        <button
          onClick={onToggle}
          className={`relative h-6 w-11 rounded-full transition ${
            enabled ? "bg-emerald-500" : "bg-slate-300 dark:bg-slate-600"
          }`}
        >
          <span
            className={`absolute top-0.5 h-5 w-5 rounded-full bg-white transition-all ${
              enabled ? "left-[1.4rem]" : "left-0.5"
            }`}
          />
        </button>
      </div>

      {enabled && recordCfg && (
        <div className="px-4 pb-3">
          <div className="inline-flex rounded-lg border border-slate-200 dark:border-slate-700 p-0.5 text-xs">
            <Seg active={!isSelected} onClick={() => onSetScope(null)}>
              Share all
            </Seg>
            <Seg
              active={isSelected}
              onClick={() => onSetScope(selection || [])}
            >
              Only selected
            </Seg>
          </div>
          {isSelected && (
            <RecordPicker
              cfg={recordCfg}
              selected={selection}
              onChange={onSetScope}
            />
          )}
        </div>
      )}
    </div>
  );
}

function Seg({ active, onClick, children }) {
  return (
    <button
      onClick={onClick}
      className={`px-3 py-1 rounded-md font-medium transition ${
        active
          ? "bg-emerald-500 text-white"
          : "text-slate-500 hover:text-slate-700 dark:hover:text-slate-300"
      }`}
    >
      {children}
    </button>
  );
}

function RecordPicker({ cfg, selected, onChange }) {
  const [items, setItems] = useState(null);
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get(cfg.url);
        if (alive) setItems(data[cfg.key] || []);
      } catch (_) {
        if (alive) setItems([]);
      }
    })();
    return () => {
      alive = false;
    };
  }, [cfg.url, cfg.key]);

  if (items === null)
    return <div className="text-xs text-slate-400 mt-2">Loading records…</div>;
  if (!items.length)
    return (
      <div className="text-xs text-slate-400 mt-2">No records to share.</div>
    );

  const sel = new Set((selected || []).map(String));
  const toggle = (id) => {
    const n = new Set(sel);
    n.has(String(id)) ? n.delete(String(id)) : n.add(String(id));
    onChange([...n]);
  };

  return (
    <div className="mt-2 space-y-1 max-h-56 overflow-y-auto rounded-lg border border-slate-100 dark:border-slate-800 p-1">
      {items.map((it) => (
        <label
          key={it.id}
          className="flex items-center gap-2.5 rounded-lg px-2 py-1.5 hover:bg-slate-50 dark:hover:bg-slate-800 cursor-pointer"
        >
          <Checkbox
            checked={sel.has(String(it.id))}
            onCheckedChange={() => toggle(it.id)}
          />
          <span className="flex-1 text-sm text-slate-800 dark:text-slate-200 truncate">
            {cfg.label(it)}
          </span>
          <span className="text-xs text-slate-400 shrink-0">{cfg.sub(it)}</span>
        </label>
      ))}
    </div>
  );
}

function PermCard({ active, onClick, icon: Icon, title, desc }) {
  return (
    <button
      onClick={onClick}
      className={`text-left flex items-start gap-3 rounded-xl border p-3 transition ${
        active
          ? "border-emerald-400 bg-emerald-50 dark:bg-emerald-500/10"
          : "border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800"
      }`}
    >
      <Icon className="h-5 w-5 shrink-0 text-emerald-600 dark:text-emerald-400 mt-0.5" />
      <div>
        <div className="text-sm font-semibold text-slate-900 dark:text-white flex items-center gap-1.5">
          {title}
          {active && <Check className="h-3.5 w-3.5 text-emerald-500" />}
        </div>
        <div className="text-xs text-slate-500">{desc}</div>
      </div>
    </button>
  );
}

function actionLabel(action, detail) {
  switch (action) {
    case "connect":
      return "Connected with advisor";
    case "reconnect":
      return "Reconnected with advisor";
    case "revoke":
      return "Revoked all access";
    case "permission_changed":
      return `Permission changed${detail ? `: ${detail.from} → ${detail.to}` : ""}`;
    case "module_enabled":
      return `Enabled sharing: ${detail?.module || ""}`;
    case "module_disabled":
      return `Disabled sharing: ${detail?.module || ""}`;
    case "scope_changed":
      return `Changed ${detail?.module || ""} sharing to ${detail?.scope || ""}`;
    case "request_created":
      return `Advisor proposed a ${detail?.operation || "change"} on ${detail?.module || ""}`;
    case "request_approved":
      return `Approved a ${detail?.operation || "change"} on ${detail?.module || ""}`;
    case "request_rejected":
      return "Rejected a change request";
    default:
      return action;
  }
}
